'use client';

import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import { Button } from '@/hen-ui/atomics/Button';
import { Link } from '@/hen-ui/atomics/Link';
import { List, ListItem } from '@/hen-ui/atomics/List';
import { type IconName } from '@/hen-ui/atomics/Icon';
import { Flex } from '@/hen-ui/composites/Flex';
import { Section } from '@/hen-ui/composites/Section';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  tocNavContentVariants,
  tocNavRowVariants,
  tocNavVariants,
  type TocNavVariantProps,
} from './toc-nav.variants';

export interface TocNavEntry {
  href: string;
  label: string;
}

export interface TocNavProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title'>,
    TocNavVariantProps {
  /** TOC items rendered in the disclosure panel below the bar. */
  items?: TocNavEntry[];
  /**
   * Children API alternative to `items` — compose with `<TocNav.Item>` so the
   * WordPress shortcode contract stays in lockstep with `TableOfContents`.
   */
  children?: React.ReactNode;

  /** "In This Article" trigger label (collapsed state). Default: "In This Article". */
  title?: string;
  /** Trigger icon (collapsed state). Default: `'list'`. */
  titleIcon?: IconName;
  /**
   * Trigger label when the disclosure panel is open. Per Figma "Sticky
   * TOC - D" expanded variant (node 4430:52888) the label flips to "Close".
   * Default: `'Close'`.
   */
  closeTitle?: string;
  /** Trigger icon when the disclosure panel is open. Default: `'x'`. */
  closeIcon?: IconName;

  /** Render the "Save this article" affordance. Default: `true`. */
  showSaveButton?: boolean;
  /** Save button label. Default: `'Save this article to your dashboard'`. */
  saveButtonText?: string;
  /** Save button icon. Default: `'heart'`. */
  saveButtonIcon?: IconName;
  /** Save button click handler. */
  onSaveClick?: React.MouseEventHandler<HTMLButtonElement>;

  /** Render the "Back to Top" affordance. Default: `true`. */
  showBackToTop?: boolean;
  /** Back to Top label. Default: `'Back to Top'`. */
  backToTopText?: string;
  /** Back to Top icon. Default: `'circle-arrow-up'`. */
  backToTopIcon?: IconName;
  /** Override the default smooth-scroll-to-top behavior. */
  onBackToTopClick?: React.MouseEventHandler<HTMLButtonElement>;

  /** Render the 3px reading-progress bar at the bottom of the nav. Default: `true`. */
  showProgress?: boolean;

  /** Controlled disclosure state for the items panel. */
  expanded?: boolean;
  /** Default disclosure state when uncontrolled. Default: `false`. */
  defaultExpanded?: boolean;
  /** Disclosure state change callback. */
  onExpandedChange?: (expanded: boolean) => void;

  /**
   * Controlled "active" item href. When set, this overrides the internal
   * scroll-spy and forces the matching item into its active state. Useful
   * for tests, Storybook, or apps that already track the current section
   * via their own router/scroll logic.
   */
  activeHref?: string | null;
  /**
   * Auto-track the current section via `IntersectionObserver` so the matching
   * item gets the active treatment (bold label, filled bullet) on desktop.
   * Default: `true`. Set to `false` to disable scroll-spy entirely (e.g.
   * when `activeHref` is supplied externally).
   */
  enableActiveTracking?: boolean;
}

export interface TocNavItemProps {
  href: string;
  /**
   * Item label. **Must be a plain string** — unlike
   * `TableOfContents.Item`, which renders arbitrary `ReactNode` children,
   * `TocNav.Item` only extracts a string label (the disclosure list is a
   * flat anchor list, so rich children have nowhere to render). Non-string
   * children resolve to an empty label. hen-site is unaffected because it
   * feeds the `items` prop via `extractTocItems`; this constraint only
   * matters for shortcode/JSX consumers using the children API.
   */
  children?: React.ReactNode;
  className?: string;
}

/**
 * `TocNav.Item` declarative form — mirrors `TableOfContents.Item` so
 * consumers can swap the two composites without changing the children
 * contract (used by the `[hen-ui-toc-item]` WordPress shortcode).
 */
export function TocNavItem(_props: TocNavItemProps): React.ReactNode {
  return null;
}
TocNavItem.displayName = 'TocNav.Item';

function resolveItemsFromChildren(
  children: React.ReactNode,
): TocNavEntry[] | undefined {
  const collected: TocNavEntry[] = [];
  let sawItem = false;
  React.Children.forEach(children, (child) => {
    if (
      React.isValidElement<TocNavItemProps>(child) &&
      typeof child.props.href === 'string'
    ) {
      sawItem = true;
      // String-only label by design — see `TocNavItemProps.children`.
      // Rich `ReactNode` children resolve to an empty label rather than
      // being cloned/rendered (the disclosure list is a flat anchor list).
      const label =
        typeof child.props.children === 'string' ? child.props.children : '';
      collected.push({ href: child.props.href, label });
    }
  });
  return sawItem ? collected : undefined;
}

/**
 * Track the "current" section so a TOC item gets the active treatment
 * as the user scrolls **through** the section's content — not just while
 * the heading itself is visible.
 *
 * Strategy: on every scroll/resize tick, walk every item's target
 * heading and pick the one whose `getBoundingClientRect().top` is the
 * largest value still at-or-above the threshold line. The threshold is
 * the **bottom edge of the TocNav itself** (`thresholdRef`), so the
 * active section flips at exactly the visual line readers perceive.
 * When no heading has crossed yet, the hook returns `null` and the
 * first item stays unhighlighted (matches the "page is at the very
 * top" state).
 *
 * Why not `IntersectionObserver`? IO marks an item active only while
 * its anchor is inside the rootMargin'd viewport. Long sections cause
 * the heading to scroll out before the next heading enters, leaving a
 * dead zone where nothing is active. Walking every heading on each
 * scroll frame (rAF-throttled) gives the "stays active until the next
 * heading crosses" behavior consumers expect from a scroll-spy.
 *
 * - rAF coalesces multiple scroll events into one DOM read per frame.
 * - Listens to `resize` too so the threshold tracks layout shifts
 *   (mobile address bar, font load, sticky chrome height changes).
 * - The hook is a no-op on SSR and when `enabled === false`.
 */
function useActiveSection(
  items: TocNavEntry[],
  enabled: boolean,
  thresholdRef: React.RefObject<HTMLElement | null>,
): string | null {
  const [activeHref, setActiveHref] = React.useState<string | null>(null);

  const itemsKey = items.map((item) => item.href).join('|');

  React.useEffect(() => {
    if (!enabled || items.length === 0 || typeof document === 'undefined') {
      return;
    }

    // Resolve every heading element once per effect run, not every
    // frame. `getElementById` is fast but still a DOM tree walk — and
    // headings are stable for the lifetime of the article, so caching
    // the lookups keeps the rAF callback to pure rect reads.
    // Headings that are not yet in the DOM (lazy-rendered sections,
    // SSR-hydration race) are filtered out; if any appear later, the
    // effect re-runs because `itemsKey` changes when the items array
    // content changes.
    const headings: Array<{ href: string; element: HTMLElement }> = [];
    for (const item of items) {
      const id = item.href.replace(/^#/, '');
      const el = id ? document.getElementById(id) : null;
      if (el) headings.push({ href: item.href, element: el });
    }
    if (headings.length === 0) {
      setActiveHref(null);
      return;
    }

    let frame = 0;
    let cancelled = false;

    const compute = () => {
      frame = 0;
      if (cancelled) return;

      // Threshold = the bottom edge of the TocNav (sticky-positioned in
      // most consumers, so this tracks the visual line under the bar).
      // Fallback to a sane default when the ref isn't wired yet (e.g.
      // first paint) or when the nav is scrolled off-screen.
      const navRect = thresholdRef.current?.getBoundingClientRect();
      const threshold = navRect ? Math.max(navRect.bottom, 0) : 160;

      let active: { href: string; top: number } | null = null;
      for (const { href, element } of headings) {
        const top = element.getBoundingClientRect().top;
        // Heading has scrolled at-or-above the threshold line. Pick the
        // candidate with the *largest* such top — i.e. the most-recently
        // passed heading. This keeps a section active for the whole
        // span between its heading and the next one.
        if (top - threshold <= 0 && (!active || top > active.top)) {
          active = { href, top };
        }
      }
      setActiveHref(active?.href ?? null);
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(compute);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // Initial computation — handles "page loaded mid-article" deep links.
    compute();

    return () => {
      cancelled = true;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
    // itemsKey re-runs the effect when the items list changes by content,
    // not by reference identity (consumers often re-create the array).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, itemsKey, thresholdRef]);

  return activeHref;
}

function useScrollProgress(enabled: boolean) {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    let frame = 0;
    let cancelled = false;

    // rAF-throttle the progress update for the same reason as
    // `useActiveSection`: scroll events fire ~60+ Hz on most devices and
    // each handler triggers a React state update + repaint of the
    // progress strip. Coalescing into one update per frame keeps the
    // hook off the main-thread hot path on long articles.
    const compute = () => {
      frame = 0;
      if (cancelled) return;
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0);
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(compute);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // Initial computation — handles "page loaded mid-article" deep links
    // and short articles where no scroll event ever fires.
    compute();

    return () => {
      cancelled = true;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled]);

  return progress;
}

function useControlledState<T>(
  controlled: T | undefined,
  defaultValue: T,
  onChange?: (value: T) => void,
): [T, (next: T) => void] {
  const [uncontrolled, setUncontrolled] = React.useState(defaultValue);
  const isControlled = controlled !== undefined;
  const value = isControlled ? controlled : uncontrolled;

  const setValue = React.useCallback(
    (next: T) => {
      if (!isControlled) setUncontrolled(next);
      onChange?.(next);
    },
    [isControlled, onChange],
  );

  return [value, setValue];
}

/**
 * TocNav — horizontal in-page navigation for long-form article pages.
 *
 * Sits below the `ArticleHeroBanner` and provides:
 *
 * - An "In This Article" trigger that reveals the TOC items panel.
 *   When the panel is open, the trigger flips to "Close" with the `x`
 *   icon (per Figma "Sticky TOC - D" expanded variant, node 4430:52888).
 * - A "Save this article to your dashboard" affordance (mirrors `ArticleHeroBanner`).
 * - A "Back to Top" smooth-scroll button.
 * - A 3px reading-progress bar that tracks document scroll.
 * - A scroll-spy that highlights the current section's TOC entry on
 *   desktop (`sm:` and up) — bullet fills in `bg-heading`, label flips
 *   to `font-bold`. Mobile renders without the bullet column and
 *   without the active-bold treatment.
 *
 * On mobile (`< sm`) the disclosure auto-collapses after an item is
 * selected so the panel doesn't overlay the section being navigated to;
 * desktop keeps it open so the scroll-spy bullet stays visible.
 *
 * Items can be passed via the `items` prop or composed with
 * `<TocNav.Item>` children — the same shape used by `TableOfContents` so
 * the `[hen-ui-toc]` shortcode contract stays consistent.
 *
 * @example Items prop (controlled disclosure)
 * ```tsx
 * <TocNav
 *   items={[
 *     { href: '#overview', label: 'Overview' },
 *     { href: '#details', label: 'Details' },
 *   ]}
 *   onSaveClick={() => saveArticle()}
 * />
 * ```
 *
 * @example With `<TocNav.Item>` children (shortcode pattern)
 * ```tsx
 * <TocNav>
 *   <TocNav.Item href='#overview'>Overview</TocNav.Item>
 *   <TocNav.Item href='#details'>Details</TocNav.Item>
 * </TocNav>
 * ```
 */
const TocNavRoot = React.forwardRef<HTMLElement, TocNavProps>(
  (
    {
      className,
      padding,
      items,
      children,
      title = 'In This Article',
      titleIcon = 'list',
      closeTitle = 'Close',
      closeIcon = 'x',
      showSaveButton = true,
      saveButtonText = 'Save this article to your dashboard',
      saveButtonIcon = 'heart',
      onSaveClick,
      showBackToTop = true,
      backToTopText = 'Back to Top',
      backToTopIcon = 'circle-arrow-up',
      onBackToTopClick,
      showProgress = true,
      expanded,
      defaultExpanded = false,
      onExpandedChange,
      activeHref: controlledActiveHref,
      enableActiveTracking = true,
      ...props
    },
    ref,
  ) => {
    const panelId = React.useId();
    const [isExpanded, setExpanded] = useControlledState(
      expanded,
      defaultExpanded,
      onExpandedChange,
    );

    const resolvedItems = items ?? resolveItemsFromChildren(children) ?? [];
    const hasItems = resolvedItems.length > 0;
    const showRightCluster = showSaveButton || showBackToTop;
    const justify = hasItems ? 'between' : showRightCluster ? 'end' : 'start';
    const progress = useScrollProgress(showProgress);
    // `padding` currently drives only vertical spacing (`py-md` / `pb-md`).
    // Horizontal alignment of the inner rows is handled by `max-w-content`
    // + `mx-auto` so the progress bar can stay full-bleed against the
    // viewport edges per the Figma "Sticky TOC - D" reference.
    const isPadded = padding !== 'flush';

    // Match Figma "Sticky TOC - D" (node 4430:52861): control buttons render at
    // content height (22px) using b5 typography, with 18px icons. `flush` size
    // strips the atomic Button's default 40px (`h-10`) hit area so the bar
    // hugs the controls row tightly.
    //
    // The `[&_svg]:!size-toc-icon` descendant selector overrides the
    // icon size that the `Button` atomic injects via `iconVariants` (it
    // does not expose a per-button icon-size prop). The `!important`
    // is required because the atomic class lands on the same SVG.
    // `--size-toc-icon` is tokenized in `tailwind.config.ts` (18px,
    // confirmed in Figma node 4430:52861).
    const controlButtonClassName =
      'text-b5 leading-b5 [&_svg]:!size-toc-icon [&_[data-hen-icon]]:!size-toc-icon';

    // Trigger swap per Figma "Sticky TOC - D" expanded variant
    // (node 4430:52888): label "In This Article" → "Close", icon `list` → `x`.
    const triggerLabel = isExpanded ? closeTitle : title;
    const triggerIcon = isExpanded ? closeIcon : titleIcon;

    // Scroll-spy: only run the observer when consumers haven't supplied an
    // explicit `activeHref` AND active tracking is enabled. Disabled when
    // there are no items.
    const trackingEnabled =
      enableActiveTracking && controlledActiveHref === undefined && hasItems;
    // Internal ref to the `<nav>` itself so the scroll-spy can use the
    // bar's live bottom edge as the active-section threshold. Merged
    // with the public `forwardRef` via `setNavRef` below.
    const navRef = React.useRef<HTMLElement | null>(null);
    const setNavRef = React.useCallback(
      (node: HTMLElement | null) => {
        navRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLElement | null>).current = node;
        }
      },
      [ref],
    );
    const observedActiveHref = useActiveSection(
      resolvedItems,
      trackingEnabled,
      navRef,
    );
    const activeHref =
      controlledActiveHref !== undefined
        ? controlledActiveHref
        : observedActiveHref;

    const handleTriggerClick = React.useCallback(() => {
      if (!hasItems) return;
      setExpanded(!isExpanded);
    }, [hasItems, isExpanded, setExpanded]);

    const handleBackToTopClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (onBackToTopClick) {
          onBackToTopClick(event);
          if (event.defaultPrevented) return;
        }
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
      [onBackToTopClick],
    );

    const handleItemClick = React.useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
        const id = href.replace(/^#/, '');
        if (!id || typeof document === 'undefined') return;
        const target = document.getElementById(id);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (typeof window !== 'undefined') {
          // Intentional — `replaceState` (not `pushState`) so the in-page
          // hash navigation does not pollute the browser history stack
          // with one entry per TOC click. The default anchor behavior
          // would also push history; we suppress it via `preventDefault`
          // above so we can drive the smooth scroll ourselves and then
          // sync the URL hash for shareable links / refresh-to-section
          // without enabling Next.js's full-route SPA navigation.
          window.history.replaceState(null, '', href);
        }
        // Collapse the disclosure on mobile after navigating. Below the
        // `sm` breakpoint (640px) there is no bullet column and the open
        // panel overlays article content, so auto-closing returns the
        // reader to the section they jumped to — the standard mobile
        // disclosure-TOC pattern. Desktop keeps the panel open on purpose
        // so the scroll-spy active bullet stays visible as part of the
        // persistent nav. Guarded for jsdom where `matchMedia` is absent.
        const isMobileViewport =
          typeof window !== 'undefined' &&
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(max-width: 639.98px)').matches;
        if (isMobileViewport) {
          setExpanded(false);
        }
      },
      [setExpanded],
    );

    const paddingVariant: TocNavVariantProps['padding'] = isPadded
      ? 'padded'
      : 'flush';

    return (
      <nav
        ref={setNavRef}
        aria-label={title}
        className={cn(tocNavVariants(), className)}
        data-hen-id={COMPONENT_IDS.TocNav}
        {...props}
      >
        {/* Mirror the article layout's container chain so the controls and
            disclosure list align with the article body at every viewport:
              outer wrapper -> `px-page-sides` (16px mobile, 64px desktop)
              inner rows    -> `max-w-content mx-auto` (caps line length)
            The progress bar lives outside this wrapper so it stays
            full-bleed against the screen edges. */}
        <div className={cn(tocNavContentVariants({ padding: paddingVariant }))}>
          <Flex
            direction='row'
            wrap='wrap'
            align='center'
            justify={justify}
            className={cn(
              tocNavRowVariants({ spacing: isPadded ? 'controls' : 'none' }),
              'gap-y-1',
            )}
          >
            {hasItems && (
              <Button
                type='button'
                variant='textOnlyNeutral'
                size='flush'
                className={controlButtonClassName}
                iconBefore={triggerIcon}
                aria-expanded={isExpanded}
                aria-controls={panelId}
                onClick={handleTriggerClick}
              >
                {triggerLabel}
              </Button>
            )}

            {showRightCluster && (
              <Flex direction='row' align='center' gap='md' wrap='wrap'>
                {showSaveButton && (
                  <Button
                    type='button'
                    variant='textOnlyNeutral'
                    size='flush'
                    iconAfter={saveButtonIcon}
                    className={cn(controlButtonClassName, 'font-normal')}
                    onClick={onSaveClick}
                  >
                    <span className='hidden sm:inline'>{saveButtonText}</span>
                    <span className='sr-only sm:hidden'>{saveButtonText}</span>
                  </Button>
                )}
                {showBackToTop && (
                  <Button
                    type='button'
                    variant='textOnlyNeutral'
                    size='flush'
                    className={controlButtonClassName}
                    iconAfter={backToTopIcon}
                    onClick={handleBackToTopClick}
                  >
                    {backToTopText}
                  </Button>
                )}
              </Flex>
            )}
          </Flex>

          {hasItems && (
            <Section
              as='div'
              padding='none'
              gap='min'
              id={panelId}
              role='region'
              aria-label={title}
              hidden={!isExpanded}
              className={cn(
                'gap-0',
                // Section's `flex` overrides the HTML `hidden` attr — class wins.
                !isExpanded && 'hidden',
                tocNavRowVariants({
                  spacing: isExpanded && isPadded ? 'disclosure' : 'none',
                }),
              )}
            >
              <List bulletStyle='none' className='gap-xs p-0 m-0'>
                {resolvedItems.map((item) => {
                  const isActive = activeHref === item.href;
                  return (
                    <ListItem
                      key={item.href}
                      className='flex items-start gap-xxs text-heading !leading-none'
                    >
                      {/* Bullet column (desktop only) — `hidden sm:flex`
                          keeps the visual indicator off the mobile
                          presentation per the Figma spec. The
                          `toc-bullet-row` height/width tokens match the
                          b5 line-height so the dot baselines with the
                          label (see `tailwind.config.ts`). */}
                      <Section
                        as='div'
                        padding='none'
                        gap='min'
                        aria-hidden='true'
                        className='hidden sm:flex h-toc-bullet-row w-toc-bullet-row items-center justify-center shrink-0 gap-0'
                      >
                        <Section
                          as='div'
                          padding='none'
                          gap='min'
                          className={cn(
                            'size-toc-bullet-dot rounded-full transition-colors duration-fast gap-0',
                            isActive ? 'bg-secondary-700' : 'bg-neutral-200',
                          )}
                        />
                      </Section>
                      <Link
                        href={item.href}
                        onClick={(event) => handleItemClick(event, item.href)}
                        aria-current={isActive ? 'location' : undefined}
                        underline='none'
                        className={cn(
                          'font-body text-b5 leading-b5 text-heading',
                          'transition-colors duration-fast hover:text-primary-700',
                          'focus-visible:outline-none focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-offset-focus focus-visible:ring-offset-1 rounded-sm',
                          // Active-state bold weight is desktop-only
                          // (sm: and up) per the Figma spec.
                          isActive && 'sm:font-bold',
                        )}
                      >
                        {item.label}
                      </Link>
                    </ListItem>
                  );
                })}
              </List>
            </Section>
          )}
        </div>

        {showProgress && (
          <Section
            as='div'
            padding='none'
            gap='min'
            role='progressbar'
            aria-label='Reading progress'
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            className='h-toc-progress w-full bg-neutral-50 overflow-hidden gap-0'
          >
            <Section
              as='div'
              padding='none'
              gap='min'
              className='h-full bg-secondary-700 transition-[width] duration-fast ease-out gap-0'
              style={{ width: `${progress * 100}%` }}
            />
          </Section>
        )}
      </nav>
    );
  },
);

TocNavRoot.displayName = 'TocNav';

export const TocNav = Object.assign(TocNavRoot, { Item: TocNavItem });
